import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { getCostpointLabel } from '../utils/costpointLabels';

// Types from the new budget system
type CostpointType = 'cs' | 'pubg' | 'admin' | 'other';
type BudgetPeriodType = 'H1' | 'H2' | 'ANNUAL';
type BudgetPeriodStatus = 'draft' | 'active' | 'locked' | 'archived';
type CostpointBudgetStatus = 'not_started' | 'draft' | 'ready' | 'no_budget';

interface Tag {
  id: number;
  name: string;
  display_name: string;
  usage_count: number;
  created_at: string;
}

interface BudgetPeriod {
  id: number;
  period_type: BudgetPeriodType;
  year: number;
  start_date: string;
  end_date: string;
  status: BudgetPeriodStatus;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface BudgetItem {
  id: number;
  budget_period_id: number;
  costpoint: CostpointType;
  type: 'income' | 'expenses';
  description: string;
  amount: number;
  status: 'draft' | 'active';
  created_by: string;
  tags: Tag[];
  created_at: string;
  updated_at: string;
}

interface CostpointBudgetSummary {
  costpoint: CostpointType;
  status: CostpointBudgetStatus;
  income_items: BudgetItem[];
  expenses_items: BudgetItem[];
  total_income: number;
  total_expenses: number;
  net_budget: number;
  item_count: number;
}

const CostpointBudgetPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<BudgetPeriod | null>(null);
  const [summaries, setSummaries] = useState<CostpointBudgetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch budget period details and all costpoint summaries
      const [periodResponse, summariesResponse] = await Promise.all([
        fetch(apiUrl(`budgets/periods/${id}`), { credentials: 'include' }),
        fetch(apiUrl(`budgets/periods/${id}/costpoints/summaries`), {
          credentials: 'include',
        }),
      ]);

      if (!periodResponse.ok || !summariesResponse.ok) {
        throw new Error('Failed to fetch budget data');
      }

      const periodData = await periodResponse.json();
      const summariesData = await summariesResponse.json();

      setPeriod(periodData);
      setSummaries(summariesData);
    } catch (error) {
      console.error('Failed to fetch budget data:', error);
      setError('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchData();
  }, [id, fetchData]);

  const handleCommitBudget = async () => {
    if (!id || !period) return;

    const notReady = summaries.filter((s) => s.status !== 'ready' && s.status !== 'no_budget');
    if (notReady.length > 0) {
      alert(
        `Cannot commit budget. The following costpoints are not ready: ${notReady
          .map((s) => s.costpoint.toUpperCase())
          .join(', ')}`
      );
      return;
    }

    if (
      !confirm(
        'Are you sure you want to commit this budget period? This will make all budget items active and cannot be undone.'
      )
    ) {
      return;
    }

    try {
      const response = await fetch(apiUrl(`budgets/periods/${id}/commit`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (response.ok) {
        alert('Budget period committed successfully!');
        await fetchData(); // Refresh data
      } else {
        const error = await response.json();
        alert(`Failed to commit budget: ${error.message}`);
      }
    } catch (error) {
      console.error('Failed to commit budget:', error);
      alert('Failed to commit budget period');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatPeriodType = (type: BudgetPeriodType) => {
    switch (type) {
      case 'H1':
        return 'H1 (Jan-Jun)';
      case 'H2':
        return 'H2 (Jul-Dec)';
      case 'ANNUAL':
        return 'Annual';
      default:
        return type;
    }
  };

  const getStatusColor = (status: CostpointBudgetStatus) => {
    switch (status) {
      case 'not_started':
        return 'bg-gray-100 text-gray-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'no_budget':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: CostpointBudgetStatus) => {
    switch (status) {
      case 'not_started':
        return 'Not Started';
      case 'draft':
        return 'Draft';
      case 'ready':
        return 'Ready';
      case 'no_budget':
        return 'No Budget';
      default:
        return status;
    }
  };

  const getCostpointColor = (costpoint: CostpointType) => {
    switch (costpoint) {
      case 'cs':
        return 'bg-blue-500';
      case 'pubg':
        return 'bg-green-500';
      case 'admin':
        return 'bg-purple-500';
      case 'other':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading budget data...</p>
        </div>
      </div>
    );
  }

  if (error || !period) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Budget period not found'}</p>
          <button
            onClick={() => navigate('/budget')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Back to Budget Overview
          </button>
        </div>
      </div>
    );
  }

  const totalIncome = summaries.reduce((sum, s) => sum + s.total_income, 0);
  const totalExpenses = summaries.reduce((sum, s) => sum + s.total_expenses, 0);
  const netBudget = totalIncome - totalExpenses;
  const allReady = summaries.every((s) => s.status === 'ready' || s.status === 'no_budget');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Costpoint Budget Management</h1>
              <p className="text-gray-600 mt-2">
                {formatPeriodType(period.period_type)} {period.year} -{' '}
                {new Date(period.start_date).toLocaleDateString('fi-FI')} to{' '}
                {new Date(period.end_date).toLocaleDateString('fi-FI')}
              </p>
              <div className="mt-2">
                <span
                  className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                    period.status as unknown as CostpointBudgetStatus
                  )}`}
                >
                  {period.status.toUpperCase()}
                </span>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/budget')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Back to Overview
              </button>
              {period.status === 'draft' && allReady && (
                <button
                  onClick={handleCommitBudget}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                >
                  Commit Budget
                </button>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500">Total Income</h3>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totalIncome)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500">Total Expenses</h3>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(totalExpenses)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500">Net Budget</h3>
              <p
                className={`text-2xl font-bold ${
                  netBudget >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(netBudget)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500">Completion</h3>
              <p className="text-2xl font-bold text-gray-900">
                {summaries.filter((s) => s.status === 'ready' || s.status === 'no_budget').length}/
                {summaries.length}
              </p>
              <p className="text-sm text-gray-500">costpoints ready</p>
            </div>
          </div>

          {/* Costpoint Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {summaries.map((summary) => (
              <div
                key={summary.costpoint}
                className="bg-white rounded-lg shadow-md overflow-hidden"
                data-testid={`costpoint-card-${summary.costpoint}`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div
                        className={`w-10 h-10 ${getCostpointColor(
                          summary.costpoint
                        )} rounded-md flex items-center justify-center mr-3`}
                      >
                        <span className="text-white text-sm font-bold">
                          {summary.costpoint.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {getCostpointLabel(summary.costpoint)}
                        </h3>
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            summary.status
                          )}`}
                        >
                          {getStatusText(summary.status)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Income Items:</span>
                      <span className="text-sm font-medium">{summary.income_items.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Expense Items:</span>
                      <span className="text-sm font-medium">{summary.expenses_items.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Income:</span>
                      <span className="text-sm font-medium text-green-600">
                        {formatCurrency(summary.total_income)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Expenses:</span>
                      <span className="text-sm font-medium text-red-600">
                        {formatCurrency(summary.total_expenses)}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-sm font-medium text-gray-900">Net Budget:</span>
                      <span
                        className={`text-sm font-bold ${
                          summary.net_budget >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {formatCurrency(summary.net_budget)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={() =>
                        navigate(`/budget/periods/${id}/costpoints/${summary.costpoint}/edit`)
                      }
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
                      data-testid={`edit-budget-button-${summary.costpoint}`}
                    >
                      {period.status === 'locked' || period.status === 'active'
                        ? 'View Budget'
                        : summary.status === 'not_started'
                          ? 'Start Budget'
                          : 'Edit Budget'}
                    </button>
                    {summary.status === 'draft' &&
                      summary.item_count > 0 &&
                      period.status === 'draft' && (
                        <button
                          onClick={async () => {
                            try {
                              await fetch(
                                apiUrl(
                                  `budgets/periods/${id}/costpoints/${summary.costpoint}/status`
                                ),
                                {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  credentials: 'include',
                                  body: JSON.stringify({
                                    status: 'ready',
                                  }),
                                }
                              );
                              await fetchData();
                            } catch (error) {
                              console.error('Failed to mark as ready:', error);
                            }
                          }}
                          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors text-sm"
                        >
                          Mark Ready
                        </button>
                      )}
                    {summary.status === 'not_started' && period.status === 'draft' && (
                      <button
                        onClick={async () => {
                          try {
                            await fetch(
                              apiUrl(
                                `budgets/periods/${id}/costpoints/${summary.costpoint}/status`
                              ),
                              {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                                body: JSON.stringify({
                                  status: 'no_budget',
                                }),
                              }
                            );
                            await fetchData();
                          } catch (error) {
                            console.error('Failed to mark as no budget:', error);
                          }
                        }}
                        className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors text-sm"
                      >
                        No Budget
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Status Information */}
          {!allReady && (
            <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Budget Not Ready for Commit
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      All costpoints must be marked as "Ready" or "No Budget" before you can commit
                      the budget period.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostpointBudgetPage;
